import { useCallback } from "react";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";

const axios = require("axios").default;

const snugAuthBaseURL = process.env.REACT_APP_SNUG_AUTH_URL;

export enum AuthGenericPath {
  SIGNUP = "user/create/",
}

interface HasId {
  id: string | number | null;
}

export const auth_api_instance = axios.create({
  baseURL: snugAuthBaseURL,
});

export const signUp = async (email: string, password: string, name: string) => {
  const response = await auth_api_instance.post(AuthGenericPath.SIGNUP, {
    email: email.toLowerCase(),
    password: password,
    first_name: name,
  });
  return response.data.data;
};

export const sendVerifyEmailEmail = async (
  username: string,
  pwBypass?: boolean,
  redirectPath?: string,
) => {
  const response = await auth_api_instance.post("user/email-verify/", {
    username: username.toLowerCase(),
    pwBypass: pwBypass,
    ...(redirectPath ? { redirect_path: redirectPath } : {}),
  });
  return response;
};
// Verify / invite links are single-use and expire. Turns the API's reason into what to tell the person.
export const verifyLinkErrorMessage = (error: any): string => {
  const data = error?.response?.data;
  if (data?.code === "expired") {
    return data?.resent
      ? "This link has expired. We've emailed you a new one."
      : "This link has expired. Please request a new one.";
  }
  return "This link has already been used or is no longer valid. If you've already set up your account, please sign in.";
};
export const emailVerify = async (
  token: string,
  password: string | null,
  first_name: string | null
) => {
  const deviceToken = getMfaDeviceToken();
  const response = await auth_api_instance.put("user/email-verify/", {
    token: token,
    password: password,
    first_name: first_name,
    ...(deviceToken ? { device_token: deviceToken } : {}),
  });
  if (deviceToken && response.data?.mfa_required) clearMfaDeviceToken();
  return response;
};

export const getUser = async (token: string) => {
  const config = {
    headers: { Authorization: "Bearer " + token },
  };
  const response = await auth_api_instance.get("user/detail/", config);
  return response.data;
};

export const getToken = async (email: string, password: string) => {
  const deviceToken = getMfaDeviceToken();
  const response = await auth_api_instance.post("token/", {
    username: email.toLowerCase(),
    password: password,
    ...(deviceToken ? { device_token: deviceToken } : {}),
  });
  if (deviceToken && response.data?.mfa_required) clearMfaDeviceToken();
  return response;
};

export const verifyToken = async (
  token: string,
  controller: AbortController
) => {
  const response = await auth_api_instance.post(
    "token/verify/",
    { token: token },
    { signal: controller.signal }
  );
  return response;
};

export const refreshToken = async (refresh: string) => {
  const response = await auth_api_instance.post("token/refresh/", {
    refresh: refresh,
  });
  return response;
};

export const resetPassWordTokenAndEmail = async (email: string) => {
  const response = await auth_api_instance.post("password_reset/", {
    email: email.toLowerCase(),
    redirect_app_domain: window.location.origin.replace('https://', '').replace('http://', '') || process.env.REACT_APP_DEFAULT_SNUG_DOMAIN,
  });
  return response;
};

export const resetPassword = async (token: string, newPassword: string) => {
  const response = await auth_api_instance.post("password_reset/confirm/", {
    token: token,
    password: newPassword,
  });
  return response;
};

//This is not currently used as we pass password changes through reset password.
export const changePassword = async () => {
  let config = null;
  if (localStorage.getItem("s_at")) {
    config = {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("s_at"),
      },
    };
  }
  return await auth_api_instance.post("password_change/", null, config);
};

export const useAddOrUpdateData = <T extends HasId>(path: string) => {
  const addOrUpdateData = useCallback(
    async (data: T): Promise<T> => {
      if (data) {
        if (data["id"] && data["id"] !== "") {
          const response = await auth_api_instance.put(path, data);
          return response.data.data as T;
        } else {
          const response = await auth_api_instance.post(path, data);
          return response.data.data as T;
        }
      }
      return Promise.reject();
    },
    [path]
  );

  return addOrUpdateData;
};

export const useGenericGetData = <T,>(
  path: string,
  readOnlyId?: number | null
) => {
  const genericGetData = useCallback(
    async (params?: any): Promise<T> => {
      const response = await auth_api_instance.get(
        readOnlyId ? path.replace("v2", `v2/ro/${readOnlyId}`) : path,
        {
          params: params,
        }
      );
      return response.data.data as T;
    },
    [path, readOnlyId]
  );

  return genericGetData;
};

auth_api_instance.interceptors.request.use((config: any) => {
  if (localStorage.getItem("s_at")) {
    config.headers.Authorization = "Bearer " + localStorage.getItem("s_at");
  }
  return config;
});

export interface APIKey {
  id: string;
  name: string;
  is_active: boolean;
  expires_at: string | null;
  is_expired: boolean;
  is_valid: boolean;
  last_used_at: string | null;
  created_at: string;
  // IPs or CIDR ranges the key works from; empty means any IP.
  allowed_ips?: string[];
}

export interface APIKeyCreated extends APIKey {
  key: string;
}

export const listAPIKeys = async (): Promise<APIKey[]> => {
  const response = await auth_api_instance.get("user/api-keys/");
  return response.data.data;
};

export const createAPIKey = async (
  name: string,
  allowed_ips: string[] = []
): Promise<APIKeyCreated> => {
  const response = await auth_api_instance.post("user/api-keys/", {
    name,
    allowed_ips,
  });
  return response.data;
};

export const deleteAPIKey = async (id: string): Promise<void> => {
  await auth_api_instance.delete(`user/api-keys/${id}/`);
};

export const updateAPIKeyAllowedIps = async (
  id: string,
  allowed_ips: string[]
): Promise<APIKey> => {
  const response = await auth_api_instance.patch(`user/api-keys/${id}/`, {
    allowed_ips,
  });
  return response.data;
};

// Multi-factor authentication

export type MfaKind = "totp" | "email" | "passkey";

// What the token endpoint returns instead of tokens when the user has MFA set up.
export interface MfaChallenge {
  mfa_required: true;
  mfa_token: string;
  methods: MfaKind[];
  email_hint?: string;
  email_sent?: boolean;
}

export interface MfaDevice {
  id: string;
  kind: MfaKind;
  // Passkeys only: the name the user gave it and the site it works on.
  name?: string | null;
  domain?: string | null;
  created_at: string;
  last_used_at: string | null;
}

export interface MfaStatus {
  enrolled: boolean;
  // Whether this session signed in with a code (needed to change MFA or API keys).
  session_mfa: boolean;
  devices: MfaDevice[];
  recovery_codes_remaining: number;
  // Devices that skip the code for 30 days.
  trusted_devices?: number;
}

export interface MfaSetupStart {
  setup_token: string;
  kind: MfaKind;
  secret?: string;
  otpauth_uri?: string;
  email_hint?: string;
}

export interface MfaTokens {
  access: string;
  refresh: string;
  recovery_codes_remaining?: number;
  // Only when the user asked to remember this device.
  device_token?: string;
}

// "Remember this device for 30 days": the token lets password / email-link sign-ins here skip the code.
// It stays through sign-out and network switching; it's cleared when the API stops honoring it.
const MFA_DEVICE_TOKEN_KEY = "snug_mfa_device_token";

export const getMfaDeviceToken = (): string | null => {
  try {
    return localStorage.getItem(MFA_DEVICE_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const storeMfaDeviceToken = (token?: string | null): void => {
  if (!token) return;
  try {
    localStorage.setItem(MFA_DEVICE_TOKEN_KEY, token);
  } catch {
    // Storage blocked: the user just gets asked for a code next time.
  }
};

export const clearMfaDeviceToken = (): void => {
  try {
    localStorage.removeItem(MFA_DEVICE_TOKEN_KEY);
  } catch {
    // nothing stored
  }
};

export const verifyMfaLogin = async (
  mfa_token: string,
  code: string,
  remember_device: boolean = false
): Promise<MfaTokens> => {
  const response = await auth_api_instance.post("mfa/login/verify/", {
    mfa_token,
    code: code.trim(),
    remember_device,
  });
  return response.data;
};

export const getMfaPasskeyLoginOptions = async (
  mfa_token: string
): Promise<PublicKeyCredentialRequestOptionsJSON> => {
  const response = await auth_api_instance.post("mfa/login/passkey/options/", {
    mfa_token,
  });
  return response.data.options;
};

export const verifyMfaPasskeyLogin = async (
  mfa_token: string,
  credential: AuthenticationResponseJSON,
  remember_device: boolean = false
): Promise<MfaTokens> => {
  const response = await auth_api_instance.post("mfa/login/passkey/", {
    mfa_token,
    credential,
    remember_device,
  });
  return response.data;
};

export const sendMfaLoginEmail = async (mfa_token: string): Promise<void> => {
  await auth_api_instance.post("mfa/login/email/", { mfa_token });
};

export const getMfaStatus = async (): Promise<MfaStatus> => {
  const response = await auth_api_instance.get("mfa/");
  return response.data;
};

export const startMfaSetup = async (kind: MfaKind): Promise<MfaSetupStart> => {
  const response = await auth_api_instance.post("mfa/setup/", { kind });
  return response.data;
};

export const resendMfaSetupEmail = async (setup_token: string): Promise<void> => {
  await auth_api_instance.post("mfa/setup/email/", { setup_token });
};

// Confirming swaps the session for one that carries MFA; recovery_codes is only set for the first device.
export const confirmMfaSetup = async (
  setup_token: string,
  code: string
): Promise<{ access: string; refresh: string; recovery_codes: string[] | null }> => {
  const response = await auth_api_instance.post("mfa/setup/confirm/", {
    setup_token,
    code: code.trim(),
  });
  localStorage.setItem("s_at", response.data.access);
  localStorage.setItem("s_rt", response.data.refresh);
  return response.data;
};

// Passkeys are tied to the site they're created on (this page's domain).
export const startPasskeySetup = async (): Promise<{
  setup_token: string;
  options: PublicKeyCredentialCreationOptionsJSON;
}> => {
  const response = await auth_api_instance.post("mfa/setup/passkey/options/");
  return response.data;
};

export const confirmPasskeySetup = async (
  setup_token: string,
  credential: RegistrationResponseJSON,
  name: string
): Promise<{ access: string; refresh: string; recovery_codes: string[] | null }> => {
  const response = await auth_api_instance.post("mfa/setup/passkey/", {
    setup_token,
    credential,
    name: name.trim(),
  });
  localStorage.setItem("s_at", response.data.access);
  localStorage.setItem("s_rt", response.data.refresh);
  return response.data;
};

// A starting name for a new passkey, from the device the user is on.
export const defaultPasskeyName = (): string => {
  const ua = navigator.userAgent || "";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return "iPad";
  if (/Android/.test(ua)) return "Android phone";
  if (/CrOS/.test(ua)) return "Chromebook";
  if (/Macintosh|Mac OS X/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  if (/Linux/.test(ua)) return "Linux PC";
  return "My device";
};

// The user closed or dismissed the browser's passkey prompt (or it timed out).
export const isPasskeyCancelled = (err: any): boolean =>
  err?.name === "NotAllowedError" ||
  err?.name === "AbortError" ||
  err?.code === "ERROR_CEREMONY_ABORTED";

// What to say when a passkey step fails: the API's message for API errors, else a browser-side reason.
export const passkeyErrorMessage = (err: any, fallback: string): string => {
  if (err?.isAxiosError) return mfaErrorMessage(err, fallback);
  if (err?.code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED") {
    return "This device already has a passkey for your account.";
  }
  if (err?.code === "ERROR_INVALID_DOMAIN" || err?.name === "SecurityError") {
    return "Passkeys can't be used on this site's address.";
  }
  return fallback;
};

export const removeMfaDevice = async (id: string): Promise<MfaStatus> => {
  const response = await auth_api_instance.delete(`mfa/devices/${id}/`);
  return response.data;
};

// Forgets every remembered device (including this one): they'll all need a code next sign-in.
export const forgetTrustedDevices = async (): Promise<MfaStatus> => {
  const response = await auth_api_instance.delete("mfa/trusted-devices/");
  clearMfaDeviceToken();
  return response.data;
};

export const regenerateRecoveryCodes = async (): Promise<string[]> => {
  const response = await auth_api_instance.post("mfa/recovery-codes/");
  return response.data.recovery_codes;
};

// The API's own message for an MFA error ({code, details}), else the fallback.
export const mfaErrorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  if (typeof data?.details === "string" && data.details) return data.details;
  if (err?.response?.status === 429) return "Too many attempts. Please wait a minute and try again.";
  if (!err?.response) return "We couldn't reach Snug. Check your connection and try again.";
  return fallback;
};

// The error's MFA code, e.g. "mfa_step_up" or "mfa_required".
export const mfaErrorCode = (err: any): string | undefined =>
  err?.response?.data?.code;
